import React, { useEffect, useRef, useCallback } from "react";
import { useTradePrice } from "../hooks/useTradePrice";
import {
  formatPriceFlexible as formatPrice,
  formatQuantity,
} from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  StatusIndicator,
  LayoutHeader,
} from "./smartlayout/fragments";
import {
  DEFAULT_BASE_URL,
  DEFAULT_MARKET_ID,
  DEFAULT_LIMIT,
} from "../constants";
import type { TradePriceProps } from "../sdk/types/data";
import "./smartlayout/layout.css";

function TradePrice({
  baseURL = DEFAULT_BASE_URL,
  marketId = DEFAULT_MARKET_ID,
  limit = DEFAULT_LIMIT,
}: TradePriceProps): JSX.Element {
  const {
    lastPrice,
    displayPrice,
    recentTrades,
    loading,
    error,
    isConnected,
    priceChange,
  } = useTradePrice({
    baseURL,
    marketId,
    limit,
  });

  const priceValueRef = useRef<HTMLDivElement | null>(null); // Ref for auto-sizing font

  // Auto-fit font size to container
  // Track previous price text to only adjust when text actually changes
  const prevPriceTextRef = useRef<string>("");
  const adjustFontSizeTimeoutRef = useRef<number | null>(null);

  // Shared function to calculate and apply font size
  const calculateFontSize = useCallback(() => {
    const element = priceValueRef.current;
    if (!element) return;

    const container = element.parentElement;
    if (!container) return;

    const text = element.textContent || "";
    if (!text) return;

    // Get container width (accounting for padding)
    const containerStyle = window.getComputedStyle(container);
    const containerPadding =
      parseFloat(containerStyle.paddingLeft) +
      parseFloat(containerStyle.paddingRight);
    const maxWidth = container.clientWidth - containerPadding;

    // Binary search for optimal font size (with min/max constraints)
    let minSize = 12; // Minimum readable font size
    let maxSize = 48; // Maximum font size to prevent it from being too large
    let optimalSize = minSize;

    // Create a temporary element to measure text width at different font sizes
    const measureElement = document.createElement("div");
    measureElement.style.position = "absolute";
    measureElement.style.visibility = "hidden";
    measureElement.style.whiteSpace = "nowrap";
    measureElement.style.fontFamily =
      window.getComputedStyle(element).fontFamily;
    measureElement.style.fontWeight =
      window.getComputedStyle(element).fontWeight;
    document.body.appendChild(measureElement);

    // Binary search for the largest font size that fits
    while (minSize <= maxSize) {
      const testSize = Math.floor((minSize + maxSize) / 2);
      measureElement.style.fontSize = `${testSize}px`;
      measureElement.textContent = text;
      const textWidth = measureElement.offsetWidth;

      if (textWidth <= maxWidth) {
        optimalSize = testSize;
        minSize = testSize + 1;
      } else {
        maxSize = testSize - 1;
      }
    }

    document.body.removeChild(measureElement);

    // Apply the optimal font size (only if it changed significantly to avoid unnecessary re-renders)
    const currentSize = parseFloat(window.getComputedStyle(element).fontSize);
    if (Math.abs(currentSize - optimalSize) > 0.5) {
      element.style.fontSize = `${optimalSize}px`;
    }
  }, []);

  useEffect(() => {
    if (displayPrice === null || displayPrice === undefined) return;

    const currentPriceText = formatPrice(displayPrice);

    // Only adjust font size if the price text actually changed
    if (currentPriceText === prevPriceTextRef.current) {
      return;
    }

    prevPriceTextRef.current = currentPriceText;

    // Clear any pending adjustment
    if (adjustFontSizeTimeoutRef.current !== null) {
      clearTimeout(adjustFontSizeTimeoutRef.current);
    }

    // Debounce font size adjustment to avoid excessive calculations
    adjustFontSizeTimeoutRef.current = window.setTimeout(() => {
      calculateFontSize();
    }, 150); // Debounce font size adjustments

    // Also adjust on window resize (with debounce for performance)
    let resizeTimeout: number | undefined;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        if (adjustFontSizeTimeoutRef.current !== null) {
          clearTimeout(adjustFontSizeTimeoutRef.current);
        }
        adjustFontSizeTimeoutRef.current = window.setTimeout(() => {
          calculateFontSize();
        }, 150);
      }, 100); // Debounce resize events
    };

    window.addEventListener("resize", handleResize);
    return () => {
      if (adjustFontSizeTimeoutRef.current !== null) {
        clearTimeout(adjustFontSizeTimeoutRef.current);
      }
      if (resizeTimeout !== undefined) clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [displayPrice, calculateFontSize]);

  // Memoize helper functions to prevent recreation on every render
  const formatTime = useCallback((timestamp: number): string => {
    // Validate timestamp
    if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
      return "Invalid";
    }

    // Ensure timestamp is in milliseconds
    let ts = timestamp;
    if (ts < 946684800000) {
      // Less than Jan 1, 2000 in milliseconds, assume it's in seconds
      ts = ts * 1000;
    }

    const date = new Date(ts);

    // Validate date
    if (isNaN(date.getTime())) {
      return "Invalid";
    }

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }, []);

  const getSideColor = useCallback((isBuyerMaker: boolean): string => {
    return isBuyerMaker ? "#51cf66" : "#ff6b6b";
  }, []);

  const getSideLabel = useCallback((isBuyerMaker: boolean): string => {
    return isBuyerMaker ? "BUY" : "SELL";
  }, []);

  const getSideEmoji = useCallback((isBuyerMaker: boolean): string => {
    return isBuyerMaker ? "🟢" : "🔴";
  }, []);

  if (loading) {
    return <LoadingMessage message="Loading trade price..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  // If no display price available (neither lastPrice nor markPrice), show empty message
  if (displayPrice === null || displayPrice === undefined) {
    return <EmptyMessage message="No trade data available" />;
  }

  // Use lastPrice for side indicator if available, otherwise default to neutral
  const hasLastPrice = lastPrice && lastPrice.isBuyerMaker !== undefined;
  const sideColor = hasLastPrice
    ? getSideColor(lastPrice.isBuyerMaker)
    : "#888";
  const sideLabel = hasLastPrice
    ? getSideLabel(lastPrice.isBuyerMaker)
    : "N/A";
  const sideEmoji = hasLastPrice
    ? getSideEmoji(lastPrice.isBuyerMaker)
    : "⚪";
  const flashClass =
    priceChange === "up"
      ? "flash-price-up"
      : priceChange === "down"
        ? "flash-price-down"
        : "";

  return (
    <div className="layout-container trade-price-container">
      {/* Header */}
      <LayoutHeader title={<StatusIndicator isConnected={isConnected} />}>
        <div>Last Trade Price</div>
      </LayoutHeader>

      {/* Main Price Display */}
      <div className={`trade-price-main ${flashClass}`}>
        <div
          ref={priceValueRef}
          className="trade-price-value"
          style={{ color: sideColor }}
        >
          {formatPrice(displayPrice)}
        </div>
        <div className="trade-price-side" style={{ color: sideColor }}>
          <span>{sideEmoji}</span>
          <span>{sideLabel}</span>
        </div>
      </div>

      {/* Recent Trades Table */}
      <div className="trade-price-trades-table">
        {recentTrades.length === 0 ? (
          <div className="message">No trades available</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th className="right">Size</th>
                <th className="right">Price</th>
              </tr>
            </thead>
            <tbody>
              {recentTrades.map((trade, index) => {
                const tradeColor = trade.isBuyerMaker ? "#51cf66" : "#ff6b6b";
                return (
                  <tr key={`${index}-${trade.time}-${trade.price}-${trade.size}`}>
                    <td>{formatTime(trade.time)}</td>
                    <td className="right">{formatQuantity(trade.size)}</td>
                    <td className="right price" style={{ color: tradeColor }}>
                      {formatPrice(trade.price)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default React.memo(TradePrice);
